// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import RemoveIcon from "@material-ui/icons/RemoveCircle";
import AddCircleIcon from "@material-ui/icons/AddCircle";

// Custom Components
import { ReservationCards } from "./ReservationCards";
import { SignaturesSectionViewModel } from "Custom/ViewModels/DashboardSubViewModels/SignaturesSectionViewModel";

// Custom Styles
import { DashboardPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { useDocumentsPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/DocumentPageStyling";
import { SignatureSectionStyles } from "Custom/StylesAppSpecific/DashboardSubViews/SignaturePageStyling";

interface IProps {
    propertyId: string;
}

export const SignaturesSection: React.FC<IProps> = props => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const dashboardPageStyles = DashboardPageStyles();
    const documentsPageStyles = useDocumentsPageStyles();
    const signatureSectionStyles = SignatureSectionStyles();
    const pageAnimationStyles = usePageAnimationStyles();

    // ViewModel
    const [viewModel] = useState(() => new SignaturesSectionViewModel(props.propertyId));

    // Accordion state
    const [expanded, setExpanded] = useState<string | null>(null);

    const handleAccordionChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : null);
    };

    const renderSectionHeader = (title: string) => {
        const panel = title.replace(" ", "").toLowerCase();
        const isActive = title === "Active";
        const hasOutstanding = isActive && viewModel.hasOutstandingSignatures;

        return (
            <AccordionSummary
                className={`${documentsPageStyles.accordionHeader} ${!isDesktop && documentsPageStyles.isMobileaccordionHeader} baseHeader`}
                style={{ display: "flex", alignItems: "center" }}
                aria-controls="panel-content"
                id="panel-header"
            >
                {expanded === panel ? <RemoveIcon style={{ marginRight: 10 }} /> : <AddCircleIcon style={{ marginRight: 10 }} />}
                <Typography className={documentsPageStyles.accordionTitle} variant="h5">
                    {title}
                </Typography>
                {hasOutstanding && <span className={signatureSectionStyles.statusDot} />}
            </AccordionSummary>
        );
    };

    const renderSignatureSection = (title: string) => {
        const panel = title.replace(" ", "").toLowerCase();

        return (
            <Box style={{ paddingBottom: "0px" }} className={dashboardPageStyles.workspace}>
                <Accordion className={signatureSectionStyles.accordionCard} expanded={expanded === panel} onChange={handleAccordionChange(panel)}>
                    {renderSectionHeader(title)}
                    <AccordionDetails className={signatureSectionStyles.accordionDetails}>
                        <ReservationCards viewModel={viewModel} sectionType={title} />
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    };

    const renderFormHeader = () => {
        return (
            <Box>
                <Box className={`baseHeader ${documentsPageStyles.documentHeader}`}>
                    <Typography variant="h5">Signatures</Typography>
                </Box>
            </Box>
        );
    };

    const renderItems = () => {
        return (
            <Box className={`${documentsPageStyles.documentPage} ${!isDesktop && documentsPageStyles.ismobileViewAccordionBlock} baseViewAccordionBlock`}>
                <Box style={{ padding: "12px 20px" }}>
                    {/* Active signature section */}
                    {renderSignatureSection("Active")}

                    {/* Pending signature section */}
                    {renderSignatureSection("Pending")}

                    {/* Completed signature section */}
                    {renderSignatureSection("Completed")}
                </Box>
            </Box>
        );
    };

    return useObserver(() => (
        <Box style={{ justifyContent: "unset" }} className={`${dashboardPageStyles.root}  ${documentsPageStyles.root} ${signatureSectionStyles.pageWrapper}`}>
            <Box style={{ height: "100%" }} className={`${dashboardPageStyles.workspace} ${!isDesktop && signatureSectionStyles.isMobileworkspace}`}>
                <React.Fragment>
                    {renderFormHeader()}
                    {renderItems()}
                </React.Fragment>
            </Box>
        </Box>
    ));
};
